import { setUnreadMessagesCount } from 'features/chat/chatSlice';
import {
  AttachmentFlagDto,
  AttachmentStatus,
  Conversation,
  Message,
  MessageData,
  UnreadConversationCountList,
} from 'features/chat/types';
import { WellframeApi } from 'store/api';
import { getTimeDiffInMs } from 'utils';
import { isPDFFile } from 'utils/fileUtils';

// Helper function to fetch and prepopulate attachment URLs for messages
const prepopulateAttachmentData = async (
  messageData: MessageData,
  dispatch: any,
  chatService: any,
): Promise<MessageData> => {
  // Function to fetch attachment URL for a given message using the getAttachment endpoint
  const fetchAttachmentUrl = async (message: Message): Promise<string | undefined> =>
    message.messageType === 'ATTACHMENT' && message.status !== 'DELETED'
      ? (
          await dispatch(
            chatService.endpoints.getAttachment.initiate({
              messageId: message.id,
              withThumbnail: !!message.fileType && !isPDFFile(message.fileType),
            }),
          )
        ).data
      : undefined;

  // Function to prepopulate a message with its attachment URL
  const prepopulateAttachmentURL = async (message: Message) => ({
    ...message,
    attachmentUrl: await fetchAttachmentUrl(message),
  });

  // Fetch attachment URLs for all messages and prepopulate them
  const messagesWithAttachments = await Promise.all(
    messageData.messages.map(prepopulateAttachmentURL),
  );
  return { ...messageData, messages: messagesWithAttachments };
};

export const chatService = WellframeApi.injectEndpoints({
  endpoints: (builder) => ({
    getConversationId: builder.query<Conversation, { userId: number }>({
      query: ({ userId }) => `/cable/v1/users/${userId}/default_conversation?userType=PROGRAM_USER`,
    }),
    getMessages: builder.query<MessageData, { conversationId: number; pageNumber: number }>({
      async queryFn({ conversationId, pageNumber }, { dispatch }, _extraOptions, baseQuery) {
        const { data: messageData, error } = await baseQuery({
          url: `/cable/v1/conversations/${conversationId}/messages`,
          params: {
            page: pageNumber,
            perPage: 20,
          },
        });
        if (error) return { error };
        const prepopulatedMessageData = await prepopulateAttachmentData(
          messageData as MessageData,
          dispatch,
          chatService,
        );

        // Return the message data with prepopulated attachment URLs
        return { data: prepopulatedMessageData };
      },
      // Refetch messages when the page number changes
      forceRefetch({ currentArg, previousArg }) {
        return currentArg?.pageNumber !== previousArg?.pageNumber;
      },
      async onQueryStarted({ conversationId }, { dispatch, queryFulfilled }) {
        try {
          const { data: updatedMessageData } = await queryFulfilled;
          dispatch(
            // Update the cache with the new messages
            chatService.util.upsertQueryData(
              'getNewMessages',
              { conversationId, pageNumber: 0 },
              updatedMessageData,
            ),
          );
        } catch {
          console.log('Error fetching new messages');
        }
      },
      providesTags: (result, error, arg) =>
        result
          ? [...result.messages.map(({ id }) => ({ type: 'Message' as const, id })), 'Message']
          : ['Message'],
    }),
    getNewMessages: builder.query<MessageData, { conversationId: number; pageNumber: number }>({
      async queryFn({ conversationId, pageNumber }, { dispatch }, extraOptions, baseQuery) {
        const { data: messageData, error } = await baseQuery({
          url: `/cable/v1/conversations/${conversationId}/messages`,
          params: {
            page: pageNumber,
            perPage: 20,
          },
        });
        if (error) return { error };

        // Return the message data with prepopulated attachment URLs
        const prepopulatedMessageData = await prepopulateAttachmentData(
          messageData as MessageData,
          dispatch,
          chatService,
        );

        const sortedMessages = prepopulatedMessageData.messages.sort((a, b) =>
          getTimeDiffInMs(b.createdAt, a.createdAt),
        );

        return {
          data: {
            ...prepopulatedMessageData,
            messages: sortedMessages,
          },
        };
      },
      providesTags: (result, error, arg) =>
        result
          ? [...result.messages.map(({ id }) => ({ type: 'Message' as const, id })), 'Message']
          : ['Message'],
      // Use a single cache for all messages
      serializeQueryArgs: ({ endpointName }) => endpointName,
      // Merge new messages with existing cache
      merge: (currentCache, newItems) => {
        const newMessageIds = new Set(newItems.messages.map((message) => message.id));
        const existingUniqueMessages = currentCache.messages.filter(
          (message) => !newMessageIds.has(message.id),
        );

        // Track the greatest page number they've been to
        const pageNumber = Math.max(currentCache.pageNumber, newItems.pageNumber);
        // If hasMore is false, we've reached the end of the messages
        const hasMore = currentCache.hasMore && newItems.hasMore;

        const messages = [...existingUniqueMessages, ...newItems.messages].sort((a, b) =>
          getTimeDiffInMs(b.createdAt, a.createdAt),
        );

        return {
          ...newItems,
          hasMore,
          pageNumber,
          messages,
        };
      },
    }),
    sendMessage: builder.mutation<Message, { conversationId: number; message: string }>({
      query: ({ conversationId, message }) => ({
        url: `/cable/v1/conversations/${conversationId}/messages`,
        method: 'POST',
        body: { messageText: message, messageType: 'APP' },
      }),
      // Only invalidate the Message tag if the mutation is successful
      invalidatesTags: (result) => (result ? ['Message'] : []),
    }),
    getAttachment: builder.query<string, { messageId: number; withThumbnail: boolean }>({
      query: ({ messageId, withThumbnail }) => ({
        url: `/cable/v1/messages/${messageId}/view_attachment?thumbnail=${withThumbnail}`,
        responseHandler: async (response) => {
          const blob = await response.blob();
          return URL.createObjectURL(blob);
        },
      }),
    }),
    getUnreadMessageCount: builder.query<number, { userId: number; lastViewTimeLapse: number }>({
      query: ({ userId }) => ({
        url: `/cable/v1/users/${userId}/unread_count?userType=PROGRAM_USER`,
      }),
      transformResponse: (unreadConversations: UnreadConversationCountList) =>
        unreadConversations.reduce((total, conversation) => total + conversation.unread_count, 0),
      onQueryStarted({ userId }, { dispatch, queryFulfilled }) {
        queryFulfilled
          .then(({ data }) => {
            dispatch(setUnreadMessagesCount({ unreadMessagesCount: data }));
          })
          .catch(() => {
            console.log('Error fetching unread message count');
          });
      },
    }),
    markMessagesAsRead: builder.mutation<void, { userId: number; conversationId: number }>({
      query: ({ userId, conversationId }) => ({
        url: `/cable/v1/users/${userId}/conversations/${conversationId}?userType=PROGRAM_USER`,
        method: 'PATCH',
        body: { lastViewedAt: new Date() },
      }),
    }),
    sendAttachments: builder.mutation<
      Message,
      { conversationId: number; file: File; message?: string }
    >({
      query: ({ conversationId, file, message }) => {
        const formData = new FormData();
        formData.append('file', file);
        if (message) {
          formData.append('text', message);
        }
        return {
          url: `/cable/v1/conversations/${conversationId}/add_attachment`,
          method: 'POST',
          body: formData,
        };
      },
      // Only invalidate the Message tag if the mutation is successful
      invalidatesTags: (result) => (result ? ['Message'] : []),
    }),
    deleteAttachment: builder.mutation<AttachmentStatus, { messageId: number }>({
      query: ({ messageId }) => ({
        url: `/cable/v1/messages/${messageId}/delete_attachment`,
        method: 'DELETE',
      }),
      // Only invalidate the Message tag if the mutation is successful
      invalidatesTags: (result, error, { messageId }) =>
        result ? [{ type: 'MessageAttachment', id: messageId }] : [],
    }),
    getAttachmentStatus: builder.query<AttachmentStatus, number>({
      query: (messageId) => ({
        url: `/cable/v1/messages/${messageId}/attachment_status`,
      }),
      providesTags: (result, error, messageId) => [{ type: 'MessageAttachment', id: messageId }],
    }),
    getAttachmentFlagInfo: builder.query<AttachmentFlagDto, { hospitalId: number }>({
      query: ({ hospitalId }) => ({
        url: `/cable/v1/hospitals/${hospitalId}/attachment_info`,
      }),
    }),
  }),
});

export const {
  useGetConversationIdQuery,
  useGetNewMessagesQuery,
  useGetMessagesQuery,
  useSendMessageMutation,
  useLazyGetAttachmentQuery,
  useSendAttachmentsMutation,
  useDeleteAttachmentMutation,
  useGetUnreadMessageCountQuery,
  useMarkMessagesAsReadMutation,
  useGetAttachmentStatusQuery,
  useGetAttachmentFlagInfoQuery,
} = chatService;
